import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";
import { numeroMonetarioParaExtenso } from "../../../../utils/numeroPorExtenso";
import { useAuth } from "../../../../context/auth-context";

/** ---------------------- Tipos ---------------------- */

type EstoqueConfig = {
  areaSlug: string;
  estoqueSlug: string;
  title: string;
  reportTitle: string;
  fileNamePrefix: string;
  movimentacaoPath: string;
  detalheProdutoPath: (codigo: string) => string;
};

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

export type EstoqueProduto = {
  produtoId: number | null;
  estoqueId: number | null;
  moduloId: number | null;

  posicao: string | null;
  codigo: string;
  ncm: string | null;
  nomeProduto: string;
  custoUnitario: string | null;
  quantidade: number | null;
  classificacao: string | null;
  moeda: string | null;

  estoqueCodigo: string | null;
  estoqueNome: string | null;
  moduloCodigo: string | null;
  moduloLabel: string | null;

  ocultar?: boolean;
};

export type EstoqueRowAPI = {
  produto_id?: number | string | null;
  estoque_id?: number | string | null;
  modulo_id?: number | string | null;

  codigo: string;
  nome: string;
  classificacao: string | null;
  posicao: string | null;
  quantidade_atual: number | string | null;
  custo_unitario_atual: string | number | null;
  ultimo_custo_unitario?: string | number | null;
  moeda?: string | null;
  ultima_moeda?: string | null;
  ncm?: string | null;

  estoque_codigo?: string | null;
  estoque_nome?: string | null;
  modulo_codigo?: string | null;
  modulo_label?: string | null;

  ocultar?: boolean;
};

type ListaResp = {
  ok?: boolean;
  fonte?: "cache" | "banco";
  versao?: number | null;
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  resumo?: {
    total_produtos?: number | string;
    quantidade_total?: number | string;
    valor_total_estimado?: number | string;
  };
  data: EstoqueRowAPI[];
  visiveis?: EstoqueRowAPI[];
  ocultos?: EstoqueRowAPI[];
};

/** ---------------------- Constantes API ---------------------- */

const URL_ESTOQUE =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-estoque";

const ACCESS_TOKEN_KEY = "access_token";

const POLL_FALLBACK_MS = 15000;

/** ---------------------- Config / rota ---------------------- */

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part.toLowerCase() === "p") return "p/";
      if (part.toLowerCase() === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): EstoqueConfig {
  const area = normalizeSlug(areaSlug) || "suprimentos";
  const estoque = normalizeSlug(estoqueSlug) || "estoque";

  const titleFromSlug = slugToTitle(estoque);

  return {
    areaSlug: area,
    estoqueSlug: estoque,
    title: `${slugToTitle(area)} - ${titleFromSlug}`,
    reportTitle: `Relatório de Estoque - ${titleFromSlug}`,
    fileNamePrefix: `${area}-${estoque}-Estoque`,
    movimentacaoPath: `/${area}/${estoque}/estoque/realizar-movimentacao`,
    detalheProdutoPath: (codigo) =>
      `/${area}/${estoque}/produtos/${encodeURIComponent(codigo)}`,
  };
}

function findModuloAtualFromSessao(
  sessao: any,
  config: EstoqueConfig
): ModuloAtual | null {
  const areaSlug = normalizeComparable(config.areaSlug);
  const estoqueSlug = normalizeComparable(config.estoqueSlug);

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoqueSlug] || estoqueSlug;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (areaSlug && depSlug && depSlug !== areaSlug) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoqueSlug ||
            modLabel === estoqueSlug ||
            modCodigo.includes(estoqueSlug) ||
            estoqueSlug.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  return Number.isFinite(n) ? n : null;
}

function normalizeRow(r: EstoqueRowAPI): EstoqueProduto {
  const custo = r.custo_unitario_atual ?? r.ultimo_custo_unitario ?? null;

  const moeda =
    typeof r.moeda === "string" && r.moeda.trim() !== ""
      ? r.moeda
      : typeof r.ultima_moeda === "string" && r.ultima_moeda.trim() !== ""
      ? r.ultima_moeda
      : null;

  return {
    produtoId: toNumberOrNull(r.produto_id),
    estoqueId: toNumberOrNull(r.estoque_id),
    moduloId: toNumberOrNull(r.modulo_id),

    posicao: r.posicao ?? null,
    codigo: String(r.codigo ?? ""),
    ncm: r.ncm ?? null,
    nomeProduto: String(r.nome ?? ""),
    custoUnitario: custo === null || custo === undefined ? null : String(custo),
    quantidade: toNumberOrNull(r.quantidade_atual),
    classificacao: r.classificacao ?? null,
    moeda,

    estoqueCodigo: r.estoque_codigo ?? null,
    estoqueNome: r.estoque_nome ?? null,
    moduloCodigo: r.modulo_codigo ?? null,
    moduloLabel: r.modulo_label ?? null,

    ocultar: Boolean(r.ocultar),
  };
}

function sameArray(a: EstoqueProduto[], b: EstoqueProduto[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function moedaFromItem(p: EstoqueProduto): string | null {
  return p?.moeda && String(p.moeda).trim() !== "" ? String(p.moeda) : null;
}

function moedaSymbol(moeda?: string | null): string {
  if (moeda === "USD") return "$";
  if (moeda === "EUR") return "€";
  if (moeda === "BRL") return "R$";
  return "";
}

function formatCurrency(
  valor: string | number | null | undefined,
  moeda?: string | null
): string {
  if (!moeda) return "N/A";

  const n =
    typeof valor === "string" ? Number(valor.replace(",", ".")) : valor ?? 0;

  if (!Number.isFinite(n)) return "N/A";

  const symbol = moedaSymbol(moeda);
  if (!symbol) return "N/A";

  return `${symbol} ${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function buildRow(p: EstoqueProduto): string[] {
  const moeda = moedaFromItem(p);

  return [
    p.posicao ?? "",
    p.codigo ?? "",
    p.ncm ?? "",
    p.nomeProduto ?? "",
    formatCurrency(p.custoUnitario, moeda),
    moeda ?? "N/A",
    String(p.quantidade ?? 0),
    p.classificacao ?? "",
  ];
}

function rowMatchesFilters(
  row: (string | number | React.ReactNode)[],
  filters: string[]
) {
  return row.every((cell, i) => {
    const filtro = normalizeText(filters[i] ?? "");
    if (!filtro) return true;

    const valor =
      typeof cell === "string" || typeof cell === "number"
        ? normalizeText(cell)
        : "";

    return valor.includes(filtro);
  });
}

/** ---------------------- HTTP helpers ---------------------- */

async function headVersao(
  moduloAtual: ModuloAtual | null,
  signal?: AbortSignal
): Promise<number | null> {
  try {
    if (!moduloAtual?.id) return null;

    const params = new URLSearchParams();
    params.set("modulo_id", String(moduloAtual.id));

    const url = `${URL_ESTOQUE}?${params.toString()}`;

    const res = await fetch(url, {
      method: "HEAD",
      headers: buildAuthHeaders(false),
      signal,
    });

    if (!res.ok) return null;

    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;

    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  moduloAtual: ModuloAtual | null,
  signal?: AbortSignal
): Promise<{
  fonte: "cache" | "banco" | undefined;
  versao: number | null;
  data: EstoqueProduto[];
  response: ListaResp | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota");
  }

  const res = await fetch(URL_ESTOQUE, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    signal,
    body: JSON.stringify({
      modulo_id: moduloAtual.id,
    }),
  });

  const parsedJson = (await res.json().catch(() => null)) as ListaResp | null;

  if (!res.ok || parsedJson?.ok === false) {
    const erro =
      (parsedJson as any)?.erro ||
      (Array.isArray((parsedJson as any)?.erros) &&
        (parsedJson as any).erros.join(" | ")) ||
      `Erro HTTP ${res.status}`;

    throw new Error(erro);
  }

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;

  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const verBody =
    parsedJson && typeof parsedJson.versao === "number"
      ? parsedJson.versao
      : null;

  const versao = Number.isFinite(verHdr as number) ? verHdr : verBody;

  const dataRaw: EstoqueRowAPI[] =
    parsedJson && Array.isArray(parsedJson.data) ? parsedJson.data : [];

  const data = dataRaw.map(normalizeRow);

  return {
    fonte,
    versao: versao ?? null,
    data,
    response: parsedJson,
  };
}

/** ---------------------- Hook genérico ---------------------- */

function useEstoqueDisponivel(
  moduloAtual: ModuloAtual | null,
  authLoading: boolean
) {
  const [itens, setItens] = useState<EstoqueProduto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, setVersao] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [meta, setMeta] = useState<ListaResp | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);
  const fetching = useRef(false);

  const setVersaoBoth = (v: number | null) => {
    versaoRef.current = v;
    setVersao(v);
  };

  async function carregar(signal?: AbortSignal) {
    fetching.current = true;

    try {
      setErro(null);

      const result = await getLista(moduloAtual, signal);

      setFonte(result.fonte);
      setVersaoBoth(result.versao);
      setMeta(result.response);

      setItens((prev) => {
        if (sameArray(prev, result.data)) return prev;
        return result.data;
      });
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar estoque.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    setLoading(true);
    setErro(null);
    setItens([]);
    setMeta(null);
    setVersaoBoth(null);

    const bootstrapController = new AbortController();

    if (authLoading) {
      return () => {
        stop.current = true;
        bootstrapController.abort();
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");
      return () => {
        stop.current = true;
        bootstrapController.abort();
      };
    }

    (async () => {
      try {
        const head = await headVersao(moduloAtual, bootstrapController.signal);

        if (head !== null) {
          setVersaoBoth(head);
        }

        await carregar(bootstrapController.signal);
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(POLL_FALLBACK_MS);

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useEstoqueDisponivel loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      bootstrapController.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, authLoading]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    itens,
    loading,
    fonte,
    versao,
    erro,
    meta,
    refresh,
  };
}

/** ---------------------- Página genérica ---------------------- */

const EstoqueGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const navigate = useNavigate();
  const { sessao, loading: authLoading } = useAuth();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const moduloAtual = useMemo(
    () => findModuloAtualFromSessao(sessao, config),
    [sessao, config]
  );

  const { itens, loading, erro, meta } = useEstoqueDisponivel(
    moduloAtual,
    authLoading
  );

  const pageLoading = authLoading || loading;

  const title = useMemo(() => {
    const estoqueNome = meta?.estoque?.nome;
    const moduloLabel = meta?.modulo?.label || moduloAtual?.label;

    if (estoqueNome) return `${slugToTitle(config.areaSlug)} - ${estoqueNome}`;
    if (moduloLabel) return `${slugToTitle(config.areaSlug)} - ${moduloLabel}`;

    return config.title;
  }, [config.areaSlug, config.title, meta, moduloAtual]);

  const headers = useMemo(
    () => [
      "Posição",
      "Código",
      "NCM",
      "Nome do Produto",
      "Custo Unitário",
      "Moeda",
      "Quantidade",
      "Classificação",
    ],
    []
  );

  const columnWidths = useMemo(
    () => ["10px", "70px", "70px", "350px", "100px", "60px", "10px", "50px"],
    []
  );

  const [mostrarZerados, setMostrarZerados] = useState(false);
  const [revelarZeradosNosFiltros, setRevelarZeradosNosFiltros] =
    useState(false);

  const [tableFilters, setTableFilters] = useState<string[]>(
    headers.map(() => "")
  );

  const [selectedColumns] = useState<boolean[]>(headers.map(() => true));

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  const filtrosAtivos = useMemo(
    () => tableFilters.some((f) => normalizeText(f) !== ""),
    [tableFilters]
  );

  useEffect(() => {
    if (!filtrosAtivos) {
      setRevelarZeradosNosFiltros(false);
    }
  }, [filtrosAtivos]);

  const itensComRows = useMemo(
    () =>
      itens.map((item) => ({
        item,
        row: buildRow(item),
      })),
    [itens]
  );

  const itensFiltradosPelosCamposDaTabela = useMemo(() => {
    return itensComRows.filter(({ row }) =>
      rowMatchesFilters(row, tableFilters)
    );
  }, [itensComRows, tableFilters]);

  const deveMostrarZerados =
    mostrarZerados || (filtrosAtivos && revelarZeradosNosFiltros);

  const itensVisiveis = useMemo(() => {
    if (deveMostrarZerados) {
      return itensFiltradosPelosCamposDaTabela.map(({ item }) => item);
    }

    return itensFiltradosPelosCamposDaTabela
      .map(({ item }) => item)
      .filter((p) => (p.quantidade ?? 0) > 0);
  }, [itensFiltradosPelosCamposDaTabela, deveMostrarZerados]);

  const rows = useMemo(() => itensVisiveis.map(buildRow), [itensVisiveis]);

  const existeSomenteZeradoNosFiltros = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return false;

    const encontrados = itensFiltradosPelosCamposDaTabela.map(
      ({ item }) => item
    );

    if (!encontrados.length) return false;

    const comSaldo = encontrados.some((p) => (p.quantidade ?? 0) > 0);
    const zerados = encontrados.some((p) => (p.quantidade ?? 0) <= 0);

    return !comSaldo && zerados;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const quantidadeItensZeradosEncontrados = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return 0;

    return itensFiltradosPelosCamposDaTabela.filter(
      ({ item }) => (item.quantidade ?? 0) <= 0
    ).length;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const itensRelatorio = useMemo(
    () => itens.filter((p) => (p.quantidade ?? 0) > 0),
    [itens]
  );

  const rowsReport = useMemo(
    () => itensRelatorio.map(buildRow),
    [itensRelatorio]
  );

  const totalProdutosRel = useMemo(
    () => itensRelatorio.reduce((acc, p) => acc + (p.quantidade ?? 0), 0),
    [itensRelatorio]
  );

  const totaisPorMoeda = useMemo(() => {
    const acc: Record<string, number> = {};

    itensRelatorio.forEach((p) => {
      const moeda = moedaFromItem(p);
      if (!moeda) return;

      const custo = Number(String(p.custoUnitario ?? 0).replace(",", "."));
      const qtd = p.quantidade ?? 0;
      const subtotal = Number.isFinite(custo * qtd) ? custo * qtd : 0;

      if (!acc[moeda]) acc[moeda] = 0;
      acc[moeda] += subtotal;
    });

    return acc;
  }, [itensRelatorio]);

  const footerLines = useMemo(() => {
    const lines: string[] = [];

    lines.push(`Total de Produtos no Estoque: ${totalProdutosRel}`);

    const entries = Object.entries(totaisPorMoeda);
    if (!entries.length) return lines;

    entries.sort(([a], [b]) => a.localeCompare(b));

    entries.forEach(([moeda, total]) => {
      const symbol = moedaSymbol(moeda);

      const formatted = `${symbol ? symbol + " " : ""}${total.toLocaleString(
        "pt-BR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;

      const extensoMoeda = numeroMonetarioParaExtenso(total, moeda);

      lines.push(
        `Valor Total em Estoque (${moeda}): ${formatted} (${extensoMoeda})`
      );
    });

    return lines;
  }, [totalProdutosRel, totaisPorMoeda]);

  const codigoIdx = headers.indexOf("Código");

  const gerarRelatorioEstoque = () => {
    generatePdfReport({
      reportTitle: meta?.estoque?.nome
        ? `Relatório de Estoque - ${meta.estoque.nome}`
        : config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações dos Produtos no Estoque",
      headers,
      rows: rowsReport,
      custoColIndex: 4,
      enabledColumns: enabledColumnIndexes,
      footerLines,
    });
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        departamento?: {
          id?: number | string | null;
          name?: string;
          slug?: string | null;
          routeKey?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = window.location.pathname.replace(/\/+$/, "");
      const basePath = `/${config.areaSlug}/${config.estoqueSlug}`;

      const pertenceAoEstoqueAtual =
        currentPath === basePath ||
        currentPath === `${basePath}/estoque` ||
        currentPath.startsWith(`${basePath}/`);

      if (!pertenceAoEstoqueAtual) return;

      gerarRelatorioEstoque();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config, headers, rowsReport, enabledColumnIndexes, footerLines, meta]);

  const handleMostrarZeradosSomenteNosFiltros = () => {
    setRevelarZeradosNosFiltros(true);
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={title} />

      {existeSomenteZeradoNosFiltros && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #e7cf9a",
            background: "#fff8e8",
            color: "#6b4f1f",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            Você possui este item ou similar com quantidade 0.
            {quantidadeItensZeradosEncontrados > 1
              ? ` Foram encontrados ${quantidadeItensZeradosEncontrados} itens zerados.`
              : ""}{" "}
            Clique em "Mostrar Itens Zerados" para exibir.
          </div>

          <button
            type="button"
            onClick={handleMostrarZeradosSomenteNosFiltros}
            style={{
              height: 34,
              padding: "0 14px",
              border: "1px solid #c9ad66",
              background: "#ffffff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#6b4f1f",
              whiteSpace: "nowrap",
            }}
          >
            Mostrar Itens Zerados
          </button>
        </div>
      )}

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {pageLoading ? (
        <div style={{ padding: 16 }}>Carregando estoque…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          filters={tableFilters}
          onFiltersChange={setTableFilters}
          onDoubleClick={(_viewIndex, rowData) => {
            const codigo = String(rowData[codigoIdx] ?? "").trim();

            if (codigo) {
              navigate(config.detalheProdutoPath(codigo));
            }
          }}
          responsiveWidths={{
            1300: {
              1: "10px",
              2: "70px",
              3: "0px",
              4: "350px",
              5: "100px",
              6: "60px",
              7: "10px",
              8: "50px",
            },
          }}
        />
      )}
    </div>
  );
};

export default EstoqueGenerico;
